/**
 * Phase 20.4: Risk → Issue conversion logic.
 *
 * Pure functions — no React, no store. The output is a deterministic pair:
 *   - the updated risk (status='occurred' plus audit fields)
 *   - the new issue (with originating_risk_id back-pointer + audit fields)
 *
 * Both records are then persisted by the store. We use soft conversion: the
 * risk row stays in the database with status='occurred', preserving original
 * estimates and history for retrospectives. The new issue is independent and
 * can be triaged like any other issue.
 */

import type { Issue, Risk } from '@/types';

/**
 * Generate a new issue id. ISS-XXXXXX (matches the existing pattern
 * elsewhere in the app — see `nextId` helpers).
 */
export function generateIssueId(): string {
  // 6 random uppercase chars/digits — collision-resistant enough for our scale
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // omits 0/O/1/I for readability
  let s = '';
  for (let i = 0; i < 6; i++) {
    s += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `ISS-${s}`;
}

export interface ConversionResult {
  updatedRisk: Risk;
  newIssue: Issue;
}

/**
 * Build the converted risk + new issue. Caller persists both.
 *
 * @param risk        The originating risk
 * @param note        Optional free-text note explaining why the risk materialized
 * @param convertedBy Auth user id who performed the conversion (or empty in localStorage mode)
 * @param now         Override "now" for deterministic tests (defaults to new Date())
 */
export function convertRiskToIssue(
  risk: Risk,
  note: string,
  convertedBy: string,
  now: Date = new Date(),
): ConversionResult {
  const nowIso = now.toISOString();
  const today = nowIso.split('T')[0];

  const newIssue: Issue = {
    id: generateIssueId(),
    title: `Materialized: ${risk.title}`,
    description: buildIssueDescription(risk, note, today),
    status: 'open',
    severity: risk.impact,
    owner: risk.owner,
    raisedDate: today,
    dueDate: '',
    // Audit trail
    originatingRiskId: risk.id,
    convertedAt: nowIso,
    convertedBy,
  };

  const updatedRisk: Risk = {
    ...risk,
    status: 'occurred',
    // Forward audit pointer
    materializedAt: nowIso,
    materializedIntoIssueId: newIssue.id,
  };

  return { updatedRisk, newIssue };
}

/**
 * Build the issue description: optional user-provided note first, then
 * original risk metadata for context.
 */
function buildIssueDescription(risk: Risk, note: string, today: string): string {
  const parts: string[] = [];

  if (note.trim()) {
    parts.push(note.trim());
    parts.push('');
    parts.push('---');
    parts.push('');
  }

  parts.push(`Materialized from risk ${risk.id} on ${today}.`);
  parts.push('');

  if (risk.description.trim()) {
    parts.push('Original risk description:');
    parts.push(risk.description.trim());
    parts.push('');
  }

  // Add the original estimates for retrospective context
  const estimates: string[] = [];
  estimates.push(`Original probability: ${risk.probability}%`);
  estimates.push(`Original impact: ${risk.impact}`);
  if (risk.exposure > 0) {
    estimates.push(`Original exposure: ${risk.exposure}`);
  }
  parts.push(`Original estimates: ${estimates.join(' · ')}`);

  return parts.join('\n');
}
